import { ChevronDown } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Menu } from 'common-src/components/base';

import { CLIENT_ROOT } from 'src/constants/paths';
import useTabs from 'src/hooks/useTabs';

import { TAB_KEYS } from '../../constants';
import { getCareOrchestrationPath } from '../../helpers';
import styles from './PlatformLink.module.scss';

const MENU_WIDTH = 240;

const PlatformLink = ({ patient, patientId, line = false }) => {
  const [paperStyles, setPaperStyles] = useState({});
  const { onSelectTab } = useTabs();
  const navigate = useNavigate();
  const resolvedPatientId = patient?.id ?? patient?.patientId ?? patientId;
  const profileHref =
    resolvedPatientId != null ? `${CLIENT_ROOT}/patients/details/${resolvedPatientId}/info` : null;
  const hasLink = Boolean(profileHref);
  const displayMrn = patient?.mrn ?? (resolvedPatientId != null ? String(resolvedPatientId) : '—');

  const onOpen = () => {
    if (!hasLink) return;
    onSelectTab({ path: profileHref });
  };

  if (line) {
    const menuItems = [
      {
        title: 'record',
        value: 'record',
        label: 'New profile view',
        description: 'Member Record: timeline and tasks',
        onClick: () =>
          navigate(
            getCareOrchestrationPath(TAB_KEYS.MEMBER_RECORD, { patientId: `${resolvedPatientId}` }),
            { state: { fromView: TAB_KEYS.READINESS } },
          ),
      },
      {
        title: 'tab',
        value: 'tab',
        label: 'Perry profile view ↗',
        description: 'Open in the Perry platform',
        onClick: onOpen,
      },
    ].map((item) => ({
      ...item,
      label: (
        <span className={styles.menuItem}>
          <span className={styles.menuItemLabel}>{item.label}</span>
          <span className={styles.menuItemHint}>{item.description}</span>
        </span>
      ),
    }));

    return (
      <Menu
        button={
          <span
            className={styles.line}
            onClick={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              setPaperStyles({
                top: rect.bottom + 6,
                left: Math.max(8, Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8)),
                marginTop: 0,
                marginLeft: 0,
              });
            }}
            role="presentation"
          >
            Platform
            <ChevronDown size={14} aria-hidden="true" />
          </span>
        }
        customOptions={menuItems}
        paperWidth={MENU_WIDTH}
        paperStyles={paperStyles}
        disabled={!hasLink}
      />
    );
  }

  return (
    <button
      type="button"
      className={`${styles.link} ${hasLink ? '' : styles.disabled}`}
      aria-disabled={!hasLink}
      onClick={onOpen}
    >
      <div className={styles.badge}>
        <span className={styles.badgeText}>M</span>
      </div>
      <div className={styles.meta}>
        <span className={styles.title}>Platform</span>
        <span className={styles.mrn}>{displayMrn}</span>
      </div>
      <span className={styles.arrow} aria-hidden="true">
        ↗
      </span>
    </button>
  );
};

PlatformLink.propTypes = {
  patient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    mrn: PropTypes.string,
  }),
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  line: PropTypes.bool,
};

export default PlatformLink;
